import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import service from '../../store/store/baseapiservice';

interface StreetVendorRegistrationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
}

interface BusinessSubCategory {
  tckt_sb_ctgry_id: number;
  tckt_sb_ctgry_nm: string;
}

interface Ward {
  wrd_id: number;
  wrd_nm: string;
  wrd_nu?: number;
}

interface Sachivalayam {
  svm_id: number;
  svm_nm: string;
  svm_apfs_cd?: string;
  wrd_id?: number;
}

interface TicketSuccessData {
  ticketNumber: string;
  assignedPersonName: string;
  assignedPersonPhone: string;
}

export default function StreetVendorRegistrationDrawer({ isOpen, onClose, currentLanguage }: StreetVendorRegistrationDrawerProps) {
  const [businessRequest, setBusinessRequest] = useState<string>(''); // '1' for Self, '0' for Others
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessSubCategory | null>(null);
  const [businessSubCategories, setBusinessSubCategories] = useState<BusinessSubCategory[]>([]);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    wrd_id: '',
    svm_id: '',
    address: '',
    description: ''
  });
  const [wards, setWards] = useState<Ward[]>([]);
  const [sachivalayams, setSachivalayams] = useState<Sachivalayam[]>([]);
  const [locationWard, setLocationWard] = useState<Ward | null>(null);
  const [locationSachivalayam, setLocationSachivalayam] = useState<Sachivalayam | null>(null);
  const [showWardDropdown, setShowWardDropdown] = useState(true);
  const [showSachivalayamDropdown, setShowSachivalayamDropdown] = useState(true);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [attachments, setAttachments] = useState<Array<{ fl_type: string; fl_name: string; base64: string }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [ticketSuccessData, setTicketSuccessData] = useState<TicketSuccessData | null>(null);

  // Get user details from localStorage
  const citizenName = localStorage.getItem('citizenName') || '';
  const citizenPhone = localStorage.getItem('citizenMobile') || '';

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (isOpen) {
      fetchWards();
      fetchCategories();
      // Reset form
      setBusinessRequest('');
      setSelectedBusiness(null);
      setFormData({
        name: '',
        phoneNumber: '',
        wrd_id: '',
        svm_id: '',
        address: '',
        description: ''
      });
      setLocationWard(null);
      setLocationSachivalayam(null);
      setShowWardDropdown(true);
      setShowSachivalayamDropdown(true);
      setSachivalayams([]);
      setSubmitError(null);
      setPhoneError(false);
      setLatitude(null);
      setLongitude(null);
      setAttachments([]);
      setShowSuccessPopup(false);
      setTicketSuccessData(null);
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      setIsLoadingCategories(true);
      // Fetch categories for ticket type 3 (e-requests)
      const response: any = await service.get('/support/grievances/ticket/categories/3');
      if (response && response.status === 200 && Array.isArray(response.data)) {
        // Find "STREET VENDORS REGISTRATION" category
        const streetVendorCategory = response.data.find((cat: any) => 
          cat.tckt_ctgry_nm && cat.tckt_ctgry_nm.toUpperCase() === 'STREET VENDORS REGISTRATION'
        );
        if (streetVendorCategory) {
          setCategoryId(streetVendorCategory.tckt_ctgry_id);
          // Fetch subcategories
          await fetchSubCategories(streetVendorCategory.tckt_ctgry_id);
        }
      }
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setSubmitError(currentLanguage === 'en' 
        ? 'Failed to load categories. Please try again.' 
        : 'వర్గాలను లోడ్ చేయడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const fetchSubCategories = async (catId: number) => {
    try {
      const response: any = await service.get(`/support/grievances/ticket/categories/${catId}/sub-categories`);
      if (response && response.status === 200 && Array.isArray(response.data)) {
        setBusinessSubCategories(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching subcategories:', err);
    }
  };

  const fetchWards = async () => {
    try {
      const response = await service.get<Ward[]>('/open/wards');
      if (response.status === 200 && Array.isArray(response.data)) {
        setWards(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching wards:', err);
    }
  };

  const fetchSachivalayams = async (wardId: number) => {
    try {
      const response: any = await service.post(`/open/ward/sachivalayams/${wardId}`, {});
      const payload =
        Array.isArray(response?.data) ? response.data :
        Array.isArray(response?.data?.data) ? response.data.data :
        [];
      if (response?.status === 200 && Array.isArray(payload)) {
        setSachivalayams(payload);
      } else {
        setSachivalayams([]);
      }
    } catch (err: any) {
      console.error('Error fetching sachivalayams:', err);
      setSachivalayams([]);
    }
  };

  const handleShareLocation = async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude: lat, longitude: lng } = position.coords;
          setLatitude(lat);
          setLongitude(lng);
          
          try {
            // Call chatbot API to get ward and sachivalayam based on location
            const response: any = await service.get(`/open/chatbot/${lat}/${lng}/sachivalayam`);
            
            if (response && response.data) {
              const locationData = response.data;
              
              // Set ward if available
              if (locationData.wrd && locationData.wrd.length > 0) {
                const ward = locationData.wrd[0];
                setLocationWard({
                  wrd_id: ward.wrd_id,
                  wrd_nu: ward.wrd_nu,
                  wrd_nm: ward.wrd_nm
                });
                setFormData(prev => ({ ...prev, wrd_id: String(ward.wrd_id) }));
                setShowWardDropdown(false);
                
                // Fetch sachivalayams for this ward
                await fetchSachivalayams(ward.wrd_id);
              }
              
              // Set sachivalayam if available
              if (locationData.svm && locationData.svm.length > 0) {
                const svm = locationData.svm[0];
                setLocationSachivalayam({
                  svm_id: svm.svm_id,
                  svm_nm: svm.svm_nm,
                  svm_apfs_cd: svm.svm_apfs_cd
                });
                setFormData(prev => ({ ...prev, svm_id: String(svm.svm_id) }));
                setShowSachivalayamDropdown(false);
              }
              
              alert(currentLanguage === 'en' 
                ? 'Location shared successfully' 
                : 'స్థానం విజయవంతంగా భాగస్వామ్యం చేయబడింది');
            }
          } catch (err: any) {
            console.error('Error fetching location data:', err);
            alert(currentLanguage === 'en' 
              ? 'Location shared but could not fetch ward/sachivalayam details' 
              : 'స్థానం భాగస్వామ్యం చేయబడింది కానీ వార్డ్/సచివాలయం వివరాలను పొందలేకపోయాము');
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert(currentLanguage === 'en' 
            ? 'Unable to get your location. Please try again.' 
            : 'మీ స్థానాన్ని పొందలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి.');
        }
      );
    } else {
      alert(currentLanguage === 'en' 
        ? 'Geolocation is not supported by your browser.' 
        : 'మీ బ్రౌజర్ జియోలొకేషన్‌ను మద్దతు ఇవ్వదు.');
    }
  };

  const handleWardChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const wardIdStr = e.target.value;
    const wardId = parseInt(wardIdStr, 10);
    setFormData({ ...formData, wrd_id: wardIdStr, svm_id: '' });
    setSachivalayams([]);
    setLocationWard(null);
    setShowWardDropdown(true);
    setLocationSachivalayam(null);
    setShowSachivalayamDropdown(true);
    
    if (!isNaN(wardId)) {
      fetchSachivalayams(wardId);
    }
  };

  const handlePhoneChange = (phone: string) => {
    setFormData({ ...formData, phoneNumber: phone });
    // Validate phone number (10 digits)
    if (/^(\+\d{1,3}[- ]?)?\d{10}$/.test(phone)) {
      setPhoneError(false);
    } else {
      setPhoneError(true);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // Check file type (images and documents)
      if (
        file.type === 'image/jpeg' || 
        file.type === 'image/png' || 
        file.type === 'image/jpg' ||
        file.type === 'application/pdf' ||
        file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const target = event.target;
          if (target && target.result) {
            setAttachments(prev => [...prev, {
              fl_type: file.type,
              fl_name: file.name,
              base64: target.result as string
            }]);
          }
        };
        reader.readAsDataURL(file);
      } else {
        alert(currentLanguage === 'en' 
          ? 'Please upload a valid file (Image or Document)' 
          : 'దయచేసి చెల్లుబాటు అయ్యే ఫైల్‌ను అప్‌లోడ్ చేయండి (చిత్రం లేదా పత్రం)');
      }
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!businessRequest) {
      setSubmitError(currentLanguage === 'en' ? 'Please select Business Request' : 'దయచేసి వ్యాపార అభ్యర్థనను ఎంచుకోండి');
      return;
    }
    if (!selectedBusiness) {
      setSubmitError(currentLanguage === 'en' ? 'Please select Business' : 'దయచేసి వ్యాపారాన్ని ఎంచుకోండి');
      return;
    }
    if (businessRequest === '0') {
      // Others - need name and phone
      if (!formData.name.trim()) {
        setSubmitError(currentLanguage === 'en' ? 'Please enter Name' : 'దయచేసి పేరు నమోదు చేయండి');
        return;
      }
      if (!formData.phoneNumber.trim() || phoneError) {
        setSubmitError(currentLanguage === 'en' ? 'Please enter valid Phone Number' : 'దయచేసి చెల్లుబాటు అయ్యే ఫోన్ నంబర్ నమోదు చేయండి');
        return;
      }
    }
    
    // Check ward and sachivalayam
    const hasWard = locationWard?.wrd_id || formData.wrd_id;
    const hasSvm = locationSachivalayam?.svm_id || formData.svm_id;
    
    if (!hasWard) {
      setSubmitError(currentLanguage === 'en' ? 'Please select Ward' : 'దయచేసి వార్డ్ ఎంచుకోండి');
      return;
    }
    if (!hasSvm) {
      setSubmitError(currentLanguage === 'en' ? 'Please select Sachivalayam' : 'దయచేసి సచివాలయం ఎంచుకోండి');
      return;
    }
    if (!formData.address.trim()) {
      setSubmitError(currentLanguage === 'en' ? 'Please enter Work Place address' : 'దయచేసి పని స్థల చిరునామా నమోదు చేయండి');
      return;
    }
    if (!formData.description.trim()) {
      setSubmitError(currentLanguage === 'en' ? 'Please describe your Request' : 'దయచేసి మీ అభ్యర్థనను వివరించండి');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Use location-based values if available, otherwise use selected dropdown values
      const finalWrdId = locationWard?.wrd_id || (formData.wrd_id ? parseInt(formData.wrd_id, 10) : 0);
      const finalSvmId = locationSachivalayam?.svm_id || (formData.svm_id ? parseInt(formData.svm_id, 10) : 0);

      if (!finalWrdId || finalWrdId === 0) {
        setSubmitError(currentLanguage === 'en' ? 'Please select Ward' : 'దయచేసి వార్డ్ ఎంచుకోండి');
        setIsSubmitting(false);
        return;
      }
      if (!finalSvmId || finalSvmId === 0) {
        setSubmitError(currentLanguage === 'en' ? 'Please select Sachivalayam' : 'దయచేసి సచివాలయం ఎంచుకోండి');
        setIsSubmitting(false);
        return;
      }

      if (!categoryId) {
        setSubmitError(currentLanguage === 'en' ? 'Category not found. Please refresh and try again.' : 'వర్గం కనుగొనబడలేదు. దయచేసి రిఫ్రెష్ చేసి మళ్లీ ప్రయత్నించండి.');
        setIsSubmitting(false);
        return;
      }

      const postData: any = {
        lng_dscn_tx: formData.description,
        Address: formData.address,
        wrd_id: finalWrdId,
        svm_id: finalSvmId,
        atchmnts: attachments,
        tckt_type_id: 3,
        tckt_ctgry_id: categoryId,
        tkt_srce_id: 11,
        tckt_sb_ctgry_id: selectedBusiness.tckt_sb_ctgry_id,
        updtctzen: businessRequest === '1' ? true : false,
      };

      // Add owner name and phone if Others
      if (businessRequest === '0') {
        postData.ownr_nm = formData.name;
        postData.ownr_ph = formData.phoneNumber;
      }

      const response: any = await service.post('/public/web/street-vendors/ticket', postData);

      if (response && response.status === 200) {
        // Extract ticket details from response
        // Street vendor endpoint returns [results, tckt_id] array
        const responseData = Array.isArray(response.data) ? response.data : response.data?.data || response.data;
        const ticketId = Array.isArray(responseData) && responseData.length > 1 
          ? responseData[1] 
          : responseData?.tckt_id || responseData?.tckt_num || responseData?.[0]?.tckt_id || 'N/A';
        
        // Try to get assigned user info from various possible response structures
        const results = Array.isArray(responseData) ? responseData[0] : responseData;
        let assignedPersonName = '';
        let assignedPersonPhone = '';
        
        // Check multiple possible response structures for assigned user info
        if (results) {
          // Check if results is an array with user info
          if (Array.isArray(results) && results.length > 0) {
            const userInfo = results.find(item => item.usr_id || item.fst_nm || item.usr_nm);
            if (userInfo) {
              assignedPersonName = userInfo.fst_nm || userInfo.usr_nm || userInfo.name || '';
              assignedPersonPhone = userInfo.mble_ph || userInfo.phone || userInfo.usr_ph || '';
            }
          } else if (typeof results === 'object') {
            // Check for assigned user in various possible fields
            const assignedUser = results.asgn_usrid || results.asgn_usr_id || results.assigned_user || results.user;
            
            if (Array.isArray(assignedUser) && assignedUser.length > 0) {
              assignedPersonName = assignedUser[0].fst_nm || assignedUser[0].usr_nm || assignedUser[0].name || '';
              assignedPersonPhone = assignedUser[0].mble_ph || assignedUser[0].phone || assignedUser[0].usr_ph || '';
            } else if (assignedUser && typeof assignedUser === 'object') {
              assignedPersonName = assignedUser.fst_nm || assignedUser.usr_nm || assignedUser.name || '';
              assignedPersonPhone = assignedUser.mble_ph || assignedUser.phone || assignedUser.usr_ph || '';
            } else if (results.fst_nm || results.usr_nm) {
              // Direct user info in results
              assignedPersonName = results.fst_nm || results.usr_nm || results.name || '';
              assignedPersonPhone = results.mble_ph || results.phone || results.usr_ph || '';
            }
          }
        }

        // Set success data and show popup
        setTicketSuccessData({
          ticketNumber: String(ticketId),
          assignedPersonName: assignedPersonName.trim(),
          assignedPersonPhone: assignedPersonPhone.trim()
        });
        setShowSuccessPopup(true);
      } else {
        throw new Error(response?.message || 'Failed to submit request');
      }
    } catch (err: any) {
      console.error('Error submitting street vendor registration:', err);
      setSubmitError(err.message || (currentLanguage === 'en' 
        ? 'Failed to submit request. Please try again.' 
        : 'అభ్యర్థనను సమర్పించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    setTicketSuccessData(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[10000]"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[10000] flex flex-col overflow-y-auto"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-5 py-4 flex items-center justify-between shadow sticky top-0 z-10">
              <p className="text-sm font-bold uppercase tracking-wide flex-1">
                STREET VENDORS REGISTRATION
              </p>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-100 transition p-1"
                aria-label="Close form"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Form Content */}
            <div className="flex-1 p-5 space-y-4">
              <p className="text-xs text-red-600 mb-2">
                {currentLanguage === 'en' 
                  ? 'Fields marked with * are mandatory.' 
                  : '* గుర్తు పెట్టిన ఫీల్డ్‌లు తప్పనిసరి.'}
              </p>

              {submitError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Business Request? */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Business Request?' : 'వ్యాపార అభ్యర్థన?'}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  <select
                    value={businessRequest}
                    onChange={(e) => setBusinessRequest(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    required
                  >
                    <option value="">{currentLanguage === 'en' ? 'Request from' : 'అభ్యర్థన నుండి'}</option>
                    <option value="1">{currentLanguage === 'en' ? 'Self' : 'స్వయం'}</option>
                    <option value="0">{currentLanguage === 'en' ? 'Others' : 'ఇతరులు'}</option>
                  </select>
                </div>

                {/* Select Business */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Select Business' : 'వ్యాపారాన్ని ఎంచుకోండి'}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  <select
                    value={selectedBusiness?.tckt_sb_ctgry_id || ''}
                    onChange={(e) => {
                      const subCat = businessSubCategories.find(cat => cat.tckt_sb_ctgry_id === parseInt(e.target.value, 10));
                      setSelectedBusiness(subCat || null);
                    }}
                    disabled={isLoadingCategories || businessSubCategories.length === 0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                  >
                    <option value="">{currentLanguage === 'en' ? 'Business' : 'వ్యాపారం'}</option>
                    {businessSubCategories.map((subCat) => (
                      <option key={subCat.tckt_sb_ctgry_id} value={subCat.tckt_sb_ctgry_id}>
                        {subCat.tckt_sb_ctgry_nm}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Personal Details of Business Applicant */}
                <div>
                  <p className="text-sm font-semibold text-orange-600 mb-3">
                    {currentLanguage === 'en' 
                      ? 'Personal Details of Business Applicant:-' 
                      : 'వ్యాపార అభ్యర్థి వ్యక్తిగత వివరాలు:-'}
                  </p>
                  
                  {businessRequest === '1' ? (
                    // Self - show user details
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
                      <p className="text-sm font-medium text-gray-900">{citizenName.toUpperCase()}</p>
                      <p className="text-sm text-gray-600">{citizenPhone}</p>
                    </div>
                  ) : businessRequest === '0' ? (
                    // Others - show input fields
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {currentLanguage === 'en' ? 'Name' : 'పేరు'}
                          <span className="text-red-600 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {currentLanguage === 'en' ? 'Phone Number' : 'ఫోన్ నంబర్'}
                          <span className="text-red-600 ml-1">*</span>
                        </label>
                        <input
                          type="tel"
                          value={formData.phoneNumber}
                          onChange={(e) => handlePhoneChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                          required
                        />
                        {phoneError && (
                          <p className="text-xs text-red-600 mt-1">
                            {currentLanguage === 'en' 
                              ? '(Please enter valid Phonenumber)' 
                              : '(దయచేసి చెల్లుబాటు అయ్యే ఫోన్ నంబర్ నమోదు చేయండి)'}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Share Location */}
                <div>
                  <p className="text-sm text-gray-700 mb-2">
                    {currentLanguage === 'en' 
                      ? 'Click Button To share Current Location' 
                      : 'ప్రస్తుత స్థానాన్ని భాగస్వామ్యం చేయడానికి బటన్ క్లిక్ చేయండి'}
                  </p>
                  <button
                    type="button"
                    onClick={handleShareLocation}
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg border border-orange-300 transition-colors"
                  >
                    {currentLanguage === 'en' ? 'Share Location' : 'స్థానాన్ని భాగస్వామ్యం చేయండి'}
                  </button>
                </div>

                {/* Select Ward */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Select Ward' : 'వార్డ్ ఎంచుకోండి'}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  {!showWardDropdown && locationWard ? (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                      <p className="text-sm text-orange-600 font-medium">
                        {locationWard.wrd_nu ? `W${locationWard.wrd_nu}` : locationWard.wrd_nm}
                      </p>
                    </div>
                  ) : (
                    <select
                      value={formData.wrd_id}
                      onChange={handleWardChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      required
                    >
                      <option value="">{currentLanguage === 'en' ? 'Ward' : 'వార్డ్'}</option>
                      {wards.map((ward) => (
                        <option key={ward.wrd_id} value={String(ward.wrd_id)}>
                          {ward.wrd_nm}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Select Sachivalayam */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Select Sachivalayam' : 'సచివాలయం ఎంచుకోండి'}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  {!showSachivalayamDropdown && locationSachivalayam ? (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                      <p className="text-sm text-orange-600 font-medium">
                        {locationSachivalayam.svm_nm}
                      </p>
                    </div>
                  ) : (
                    <select
                      value={formData.svm_id}
                      onChange={(e) => {
                        setFormData({ ...formData, svm_id: e.target.value });
                        setLocationSachivalayam(null);
                        setShowSachivalayamDropdown(true);
                      }}
                      disabled={!formData.wrd_id || sachivalayams.length === 0}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                    >
                      <option value="">{currentLanguage === 'en' ? 'Select Sachivalayam' : 'సచివాలయం ఎంచుకోండి'}</option>
                      {sachivalayams.map((svm) => (
                        <option key={svm.svm_id} value={String(svm.svm_id)}>
                          {svm.svm_nm}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Upload Signature&Photo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Upload Signature&Photo' : 'సంతకం & ఫోటోను అప్‌లోడ్ చేయండి'}
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                      multiple
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center justify-center"
                    >
                      <i className="ri-upload-cloud-2-line text-4xl text-gray-400 mb-2"></i>
                      <p className="text-sm text-gray-600">
                        {currentLanguage === 'en' 
                          ? 'Click to upload Signature & Photo' 
                          : 'సంతకం & ఫోటోను అప్‌లోడ్ చేయడానికి క్లిక్ చేయండి'}
                      </p>
                    </label>
                  </div>
                  {attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-xs text-gray-700 truncate flex-1">{attachment.fl_name}</span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="text-red-600 hover:text-red-700 ml-2"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Enter Work Place */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Enter Work Place' : 'పని స్థలం నమోదు చేయండి'}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder={currentLanguage === 'en' ? 'Complete Address' : 'పూర్తి చిరునామా'}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none"
                    required
                  />
                </div>

                {/* Describe your Request */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Describe your Request' : 'మీ అభ్యర్థనను వివరించండి'}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={currentLanguage === 'en' ? 'Describe your Request' : 'మీ అభ్యర్థనను వివరించండి'}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none"
                    required
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <motion.button
                    type="submit"
                    disabled={isSubmitting || isLoadingCategories}
                    className="w-full bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        {currentLanguage === 'en' ? 'Submitting...' : 'సమర్పిస్తోంది...'}
                      </span>
                    ) : (
                      'SUBMIT'
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>

          {/* Success Popup Modal */}
          <AnimatePresence>
            {showSuccessPopup && ticketSuccessData && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-[10001]"
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                  className="fixed inset-0 flex items-center justify-center z-[10001] pointer-events-none"
                >
                  <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full mx-4 pointer-events-auto">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-3xl"></i>
                      </div>
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-green-600 mb-2">
                          {currentLanguage === 'en' ? 'Street Vendor Registration Successful!' : 'వీధి విక్రేత నమోదు విజయవంతంగా!'}
                        </h3>
                        <p className="text-gray-700 text-base mb-3">
                          {currentLanguage === 'en'
                            ? `Ticket No: ${ticketSuccessData.ticketNumber}`
                            : `టికెట్ నంబర్: ${ticketSuccessData.ticketNumber}`}
                        </p>
                        {(ticketSuccessData.assignedPersonName || ticketSuccessData.assignedPersonPhone) ? (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600 mb-2">
                              {currentLanguage === 'en' ? 'Assigned To:' : 'కేటాయించబడింది:'}
                            </p>
                            {ticketSuccessData.assignedPersonName && (
                              <p className="text-base font-semibold text-gray-800 mb-1">
                                {ticketSuccessData.assignedPersonName}
                              </p>
                            )}
                            {ticketSuccessData.assignedPersonPhone && (
                              <p className="text-sm text-indigo-600 flex items-center">
                                <i className="ri-phone-line mr-1"></i>
                                {ticketSuccessData.assignedPersonPhone}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                              {currentLanguage === 'en' 
                                ? 'Assigned person details will be updated soon.' 
                                : 'కేటాయించిన వ్యక్తి వివరాలు త్వరలో నవీకరించబడతాయి.'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-6">
                      <motion.button
                        onClick={handleSuccessPopupClose}
                        className="w-full px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {currentLanguage === 'en' ? 'OK' : 'సరే'}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}

